import {
  BodyText,
  Button,
  ChoiceList,
  Input,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Radio,
} from '@postscript/components';
import FormikField from 'components/billing/common/FormikField';
import FormikFieldSelectMenu from 'components/billing/common/FormikFieldSelectMenu';
import {
  useGetComplianceContactInfo,
  useSetComplianceContactInfo,
} from 'components/compliance/useCompliance';
import { useGlobalModal } from 'components/GlobalModal/globalModal';
import {
  BusinessRegistrationAuthority,
  BusinessType,
  useGetBusinessRegistration,
  useUpdateBusinessRegistration,
} from 'components/onboarding/hooks/useBusinessRegistration';
import {
  COUNTRY_OPTIONS,
  SUPPORTED_COUNTRY_REGIONS,
} from 'components/onboarding/steps/constants';
import {
  getBusinessRegFieldConfig,
  getRegistrationAuthority,
  mapBusinessTypeToEnum,
} from 'components/onboarding/steps/utils';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { useResubmitTFNVerification } from '../hooks/useTFNVerification';

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  margin-top: var(--spacing-3);
`;

const DenialReasonSection = styled.div`
  margin-bottom: var(--spacing-3);
`;

const AddressRow = styled.div`
  display: flex;
  gap: var(--spacing-3);

  & > * {
    flex: 1;
  }
`;

interface FormValues {
  businessType: 'entity' | 'sole-proprietor';
  businessAddress: {
    address1: string;
    address2: string;
    city: string;
    countryCode: string;
    provinceCode: string;
    zip: string;
  };
  ein: string;
}

const getValidationSchema = () =>
  Yup.object({
    businessType: Yup.string()
      .oneOf(['entity', 'sole-proprietor'])
      .required('Select a business type'),
    ein: Yup.string().when('businessType', {
      is: 'entity',
      then: (schema) => {
        return schema.when(
          'businessAddress.countryCode',
          (cc: string, s: Yup.StringSchema) => {
            const { pattern, patternMessage, label } =
              getBusinessRegFieldConfig(cc);
            const base = pattern ? s.matches(pattern, patternMessage) : s;
            return base.required(`Enter your ${label}`);
          },
        );
      },
      otherwise: (schema) => schema.optional().nullable(),
    }),
    businessAddress: Yup.object({
      address1: Yup.string().required('Enter an address').nullable(),
      address2: Yup.string().nullable(),
      city: Yup.string().required('Enter a city').nullable(),
      countryCode: Yup.string().max(2).required('Select a country').nullable(),
      provinceCode: Yup.string()
        .when('countryCode', (cc: string, s: Yup.StringSchema) => {
          const hasRegions = Boolean(SUPPORTED_COUNTRY_REGIONS[cc]);
          return hasRegions
            ? s
                .required('Select a state/province')
                .max(3, 'Enter a two- or three-character code')
            : s.max(64);
        })
        .nullable(),
      zip: Yup.string().required('Enter a zip or postal code').nullable(),
    }),
  });

interface InvalidBusinessRegistrationModalProps {
  title?: string;
}

export default function InvalidBusinessRegistrationModal({
  title = 'Toll-Free Number verification denied',
}: InvalidBusinessRegistrationModalProps): JSX.Element {
  const { hideModal } = useGlobalModal();
  const { data: complianceContactInfo } = useGetComplianceContactInfo();
  const { mutateAsync: setComplianceContactInfo } =
    useSetComplianceContactInfo();
  const { data: businessRegistration } = useGetBusinessRegistration();
  const { mutateAsync: updateBusinessRegistration } =
    useUpdateBusinessRegistration();
  const { mutateAsync: resubmitTFNVerification } = useResubmitTFNVerification();

  const { billingAddress } = complianceContactInfo ?? {};

  const initialValues: FormValues = {
    businessType:
      businessRegistration?.businessType === BusinessType.SOLE_PROPRIETOR
        ? 'sole-proprietor'
        : 'entity',
    businessAddress: {
      address1: billingAddress?.address1 ?? '',
      address2: billingAddress?.address2 ?? '',
      city: billingAddress?.city ?? '',
      zip: billingAddress?.zip ?? '',
      countryCode:
        businessRegistration?.registrationCountry ??
        billingAddress?.countryCode ??
        'US',
      provinceCode: billingAddress?.provinceCode ?? '',
    },
    ein: businessRegistration?.registrationNumber ?? '',
  };

  const handleSubmit = async (values: FormValues) => {
    const { businessAddress, businessType, ein } = values;

    if (!complianceContactInfo) {
      return;
    }

    // Save business address to compliance contact info
    await setComplianceContactInfo({
      ...complianceContactInfo,
      billingAddress: businessAddress,
    });

    // Save business registration info
    await updateBusinessRegistration({
      businessType: mapBusinessTypeToEnum(businessType),
      registrationAuthority:
        businessType === 'sole-proprietor'
          ? BusinessRegistrationAuthority.OTHER
          : getRegistrationAuthority(businessAddress.countryCode),
      registrationNumber: ein || '',
      registrationCountry: businessAddress.countryCode,
    });

    await resubmitTFNVerification();
    hideModal();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getValidationSchema()}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur
      enableReinitialize
    >
      {({ values, setFieldValue, setFieldError, isSubmitting }) => (
        <Form>
          <ModalHeader onCancel={hideModal}>{title}</ModalHeader>
          <ModalBody>
            <DenialReasonSection>
              <BodyText bold size="small" color="var(--text-color)">
                Denial reason
              </BodyText>
              <BodyText size="small" color="var(--text-color)">
                {
                  getBusinessRegFieldConfig(values.businessAddress.countryCode)
                    .label
                }{' '}
                is missing or invalid. This is your Employer Identification
                Number, Canadian Business Number, or other Registration Number.
              </BodyText>
            </DenialReasonSection>

            <BodyText size="small" color="var(--text-color)">
              <strong>
                Please update your{' '}
                {
                  getBusinessRegFieldConfig(values.businessAddress.countryCode)
                    .label
                }
                , then resubmit.
              </strong>{' '}
              Verification typically takes up to 5 business days. Your website
              must remain live during this time.
            </BodyText>

            <FormSection>
              <ChoiceList
                id="business-type"
                legend="Business Type"
                fieldSize="medium"
                required
              >
                <Radio
                  checked={values.businessType === 'entity'}
                  id="business-type--entity"
                  label="Entity"
                  name="business-type"
                  onChange={() => {
                    setFieldValue('businessType', 'entity');
                  }}
                  value="entity"
                />
                <Radio
                  checked={values.businessType === 'sole-proprietor'}
                  id="business-type--sole-proprietor"
                  label="Sole Proprietor"
                  name="business-type"
                  onChange={() => {
                    setFieldValue('businessType', 'sole-proprietor');
                    setFieldValue('ein', '');
                    setFieldError('ein', undefined);
                  }}
                  value="sole-proprietor"
                />
              </ChoiceList>

              <FormikFieldSelectMenu
                name="businessAddress.countryCode"
                label="Where is your business located?"
                placeholder="Select a country"
                options={COUNTRY_OPTIONS}
                required
              />

              {values.businessType === 'entity' &&
                (() => {
                  const fieldConfig = getBusinessRegFieldConfig(
                    values.businessAddress.countryCode,
                  );
                  return (
                    <FormikField
                      name="ein"
                      label={fieldConfig.label}
                      placeholder={fieldConfig.placeholder}
                      type="text"
                      as={Input}
                      required
                      fieldSize="medium"
                    />
                  );
                })()}

              <FormikField
                name="businessAddress.address1"
                label="Business Address Line 1"
                type="text"
                as={Input}
                placeholder="Street address"
                fieldSize="medium"
                required
              />
              <FormikField
                name="businessAddress.address2"
                label="Business Address Line 2 (Optional)"
                type="text"
                as={Input}
                placeholder="Apartment, suite, etc."
                fieldSize="medium"
              />

              <AddressRow>
                <FormikField
                  name="businessAddress.city"
                  label="City"
                  placeholder="City"
                  type="text"
                  as={Input}
                  required
                  fieldSize="medium"
                />
                {SUPPORTED_COUNTRY_REGIONS[
                  values.businessAddress.countryCode
                ] ? (
                  <FormikFieldSelectMenu
                    name="businessAddress.provinceCode"
                    label="State/Province"
                    options={
                      SUPPORTED_COUNTRY_REGIONS[
                        values.businessAddress.countryCode
                      ]
                    }
                    required
                    openMenuMaxVisibleOptions={2}
                  />
                ) : (
                  <FormikField
                    name="businessAddress.provinceCode"
                    label="State/Province code"
                    placeholder="State/Province"
                    type="text"
                    as={Input}
                    fieldSize="medium"
                  />
                )}
                <FormikField
                  name="businessAddress.zip"
                  label="Postal Code"
                  placeholder="Postal code"
                  type="text"
                  as={Input}
                  required
                  fieldSize="medium"
                />
              </AddressRow>
            </FormSection>
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !complianceContactInfo}
            >
              Save and Resubmit
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
}
